import { useCallback } from "react";

import { useDeletePreference, useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";

/**
 * @param preferenceKey - The key of the preference to use
 * @returns The value of the preference and a setter that optimistically updates it
 */
export const usePreferenceValue = (preferenceKey: string) => {
    const { data: preference, ...queryData } = useGetPreferenceByKey(preferenceKey);

    const preferenceMutation = useSetPreference();
    const setValueAsync = useCallback(
        async (newValue: string) => {
            await preferenceMutation.mutateAsync({ key: preferenceKey, value: newValue });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [preferenceKey],
    );

    const setValue = useCallback(
        (newValue: string) => {
            setValueAsync(newValue).catch(console.error);
        },
        [setValueAsync],
    );

    const deletePreferenceMutation = useDeletePreference();
    const deleteValue = useCallback(async () => {
        if (!preference) {
            return;
        }

        await deletePreferenceMutation.mutateAsync({ id: preference.id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preference]);

    return {
        ...queryData,
        value: preference?.value,
        setValue,
        setValueAsync,
        queryData,
        deleteValue,
        isLoading: queryData.isLoading || preferenceMutation.isPending || deletePreferenceMutation.isPending,
    };
};
